// Variant B's paywall — a port of React-Membership's promo-offer-1.jsx.
//
// Same content and structure as the original: period toggle, price card with the
// "1 Month Free" headline, the benefit list, a three-step timeline of what
// happens when, then the title and the CTA.
//
// It is a REWRITE rather than a copy, because seven of the original's constructs
// do not exist in this codebase: styled-components v3 has no `createGlobalStyle`
// and no transient `$props`, react-i18next 9 has no `useTranslation`, react-router
// 4 has no `useSearchParams`, `.attrs` takes no function form, and component
// selectors need a Babel plugin this app does not have. The icons are drawn
// inline instead of importing five SVGs that live only in the other repo.

import React, { useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import { IPaywallOfferDto, IPaywallServiceDto } from "../../../shared/api/submission";
import { formatMoney } from "./money";
import { longDate } from "./dates";
import { DESKTOP } from "./breakpoints";
import { membershipPrimary } from "./membershipButton";

interface Props {
  offer: IPaywallOfferDto;
  selectedServiceId: number | null;
  onSelectService: (serviceId: number) => void;
  onCheckout: () => void;
  busy?: boolean;
}

// The original's three timeline nodes, with its own icons redrawn as inline SVG.
const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect
      x="2.5"
      y="3.5"
      width="13"
      height="12"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path d="M2.5 7h13M6 2v3M12 2v3" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M6.5 11l1.8 1.8L11.8 9.3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="6.6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9 5.8v4.4M9 12.4v.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect
      x="2"
      y="4.5"
      width="14"
      height="9"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path d="M2 8h14" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const formatDate = longDate;

const PaywallProOffer: React.FC<Props> = ({
  offer,
  selectedServiceId,
  onSelectService,
  onCheckout,
  busy,
}) => {
  const [expanded, setExpanded] = useState(false);

  const annual = offer.services.filter((s) => s.durationMonths >= 12)[0];
  const monthly = offer.services.filter((s) => s.durationMonths < 12)[0];
  const selected: IPaywallServiceDto =
    offer.services.filter((s) => s.serviceId === selectedServiceId)[0] ||
    offer.services.filter((s) => s.serviceId === offer.defaultServiceId)[0] ||
    offer.services[0];

  const annualSaving =
    annual && monthly ? Math.round(monthly.price * 12 - annual.price) : 0;
  const isAnnual = selected.durationMonths >= 12;

  const benefits = expanded
    ? offer.benefits
    : offer.benefits.slice(0, offer.collapsedBenefitCount);

  return (
    <Card>
      <Panel>
        {offer.services.length > 1 && (
          <Periods role="radiogroup" aria-label={i18next.t("Billing period")}>
            {[annual, monthly].filter(Boolean).map((service) => {
              const checked = service.serviceId === selected.serviceId;
              const yearly = service.durationMonths >= 12;
              return (
                <Period
                  key={service.serviceId}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  checked={checked}
                  onClick={() => onSelectService(service.serviceId)}
                >
                  {yearly ? i18next.t("Annual") : i18next.t("Monthly")}
                  {yearly && annualSaving > 0 && (
                    <PeriodNote>
                      {i18next.t("save {{amount}}", {
                        amount: formatMoney(annualSaving, service, {
                          whole: true,
                          symbolOnly: true,
                        }),
                      })}
                    </PeriodNote>
                  )}
                </Period>
              );
            })}
          </Periods>
        )}

        <Headline>
          {i18next.t("{{count}} Month Free", { count: selected.trialDurationMonths })}
        </Headline>
        <SubText aria-live="polite">
          {isAnnual
            ? i18next.t("Then {{amount}} Yearly", {
                amount: formatMoney(selected.price, selected),
              })
            : i18next.t("Then {{amount}} Monthly", {
                amount: formatMoney(selected.price, selected),
              })}
          <br />
          {offer.ccRequired
            ? `(${i18next.t("CC Required, Cancel Anytime!")})`
            : `(${i18next.t("Cancel Anytime!")})`}
        </SubText>

        <Benefits>
          {benefits.map((benefit) => (
            <Benefit key={benefit}>
              <Tick aria-hidden="true">✓</Tick>
              <span>{benefit}</span>
            </Benefit>
          ))}
        </Benefits>
        {offer.benefits.length > offer.collapsedBenefitCount && (
          <ExpandButton
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            {expanded ? i18next.t("Hide all benefits") : i18next.t("See all benefits")}
          </ExpandButton>
        )}

        <Timeline>
          <Step>
            <StepIcon highlight>
              <CalendarIcon />
            </StepIcon>
            <StepTitle>{i18next.t("Today")}</StepTitle>
            <StepText>
              {i18next.t("Your {{count}} day Free Trial starts", {
                count: selected.trialDurationDays,
              })}
            </StepText>
          </Step>
          <Step>
            <StepIcon>
              <AlertIcon />
            </StepIcon>
            <StepTitle>{i18next.t("1 week before trial ends")}</StepTitle>
            <StepText>{i18next.t("You will receive an email")}</StepText>
          </Step>
          <Step>
            <StepIcon>
              <CardIcon />
            </StepIcon>
            <StepTitle>{formatDate(offer.trialEndsOnUtc)}</StepTitle>
            <StepText>{i18next.t("Subscription starts")}</StepText>
          </Step>
        </Timeline>
      </Panel>

      <Description>
        <Title>
          {i18next.t("Try PRO Free for {{count}} Month!", {
            count: selected.trialDurationMonths,
          })}
        </Title>
        <Blurb>
          {i18next.t(
            "Join thousands of PRO actors using theWorkbook with all their features unlocked!"
          )}
        </Blurb>
        <PrimaryButton onClick={onCheckout} disabled={busy}>
          {busy
            ? i18next.t("One moment…")
            : i18next.t("Start Free for {{count}} Month Now!", {
                count: selected.trialDurationMonths,
              })}
        </PrimaryButton>
      </Description>
    </Card>
  );
};

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e3e8ee;
  margin: 10px 0 16px;
`;

// The original's tinted illustration frame.
const Panel = styled.div`
  background: rgba(5, 119, 240, 0.06);
  padding: 16px;
`;

const Periods = styled.div`
  display: flex;
  background: #ffffff;
  border: 1px solid #d9e2ec;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 16px;
`;

const Period: any = styled.button`
  flex: 1;
  min-height: 40px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  /* The original's checked colour. */
  background: ${(p: any) => (p.checked ? "#2ED2FF" : "transparent")};
  color: ${(p: any) => (p.checked ? "#ffffff" : "#53627c")};
`;

const PeriodNote = styled.span`
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
`;

const Headline = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #122640;
  text-align: center;
`;

const SubText = styled.div`
  font-size: 13px;
  color: #53627c;
  text-align: center;
  line-height: 20px;
  margin: 6px 0 14px;
`;

const Benefits = styled.div`
  border-top: 1px solid rgba(18, 38, 64, 0.08);
  padding-top: 12px;
`;

const Benefit = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  color: #122640;
  line-height: 22px;
`;

const Tick = styled.span`
  color: #16a34a;
  font-weight: 700;
  margin-right: 8px;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 10px 0 2px;
  color: #0f9bf0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

// Vertical on a phone, three across once there is room — the original's
// breakpoint behaviour.
const Timeline = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(18, 38, 64, 0.08);

  @media all and (min-width: ${DESKTOP}) {
    display: flex;
    justify-content: space-between;
  }
`;

const Step = styled.div`
  display: block;
  margin-bottom: 12px;

  @media all and (min-width: ${DESKTOP}) {
    flex: 1;
    margin-bottom: 0;
    padding-right: 8px;
  }
`;

const StepIcon: any = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-bottom: 6px;
  background: ${(p: any) => (p.highlight ? "#0f9bf0" : "#ffffff")};
  color: ${(p: any) => (p.highlight ? "#ffffff" : "#53627c")};
  border: 1px solid ${(p: any) => (p.highlight ? "#0f9bf0" : "#d9e2ec")};
`;

const StepTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #122640;
`;

const StepText = styled.div`
  font-size: 12px;
  color: #53627c;
  line-height: 18px;
`;

const Description = styled.div`
  padding: 16px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 6px;
  text-align: center;
`;

const Blurb = styled.div`
  font-size: 13px;
  color: #53627c;
  line-height: 20px;
  text-align: center;
  margin-bottom: 14px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

export default PaywallProOffer;
